<template>
  <v-app class="app">
    <v-main>
      <router-view/>
    </v-main>

    <ForcePasswordModal
      :visible="shouldForcePasswordChange"
      @updated="handlePasswordUpdated"
    />
    
    <!-- Guardian de sesión temporal deshabilitado -->
    <!-- <SessionGuard v-if="isAuthenticated" /> -->
  </v-app>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import ForcePasswordModal from '@/components/ForcePasswordModal.vue';
import { resolveMustChangePasswordFlag } from '@/utils/passwordFlags';
// import SessionGuard from '@/components/SessionGuard.vue';

@Component({
  components: {
    ForcePasswordModal,
    // SessionGuard,
  },
})
export default class App extends Vue {
  @Getter('isAuthenticated', { namespace: 'auth' }) isAuthenticated!: boolean;
  @Getter('getUser', { namespace: 'auth' }) currentUser!: any;

  get shouldForcePasswordChange(): boolean {
    return resolveMustChangePasswordFlag(this.currentUser);
  }

  handlePasswordUpdated() {
    console.log('🔐 Contraseña actualizada, ocultando modal.');
  }

  // ── Verificador de versión ──────────────────────────────────────────────
  // Compara el hash del index.html actual con el del servidor cada 3 minutos.
  // Si detecta un cambio (nuevo deploy), recarga la página automáticamente.
  private _versionCheckTimer: any = null;
  private _lastEtag: string = '';

  mounted() {
    if (process.env.NODE_ENV === 'production') {
      this.startVersionCheck();
    }
  }

  beforeDestroy() {
    if (this._versionCheckTimer) clearInterval(this._versionCheckTimer);
  }

  startVersionCheck() {
    // Guarda el ETag/Last-Modified inicial
    this.fetchVersionSignature().then(sig => { this._lastEtag = sig; });
    // Chequea cada 3 minutos
    this._versionCheckTimer = setInterval(async () => {
      const sig = await this.fetchVersionSignature();
      if (sig && this._lastEtag && sig !== this._lastEtag) {
        console.log('🔄 Nueva versión detectada, recargando...');
        window.location.reload();
      }
    }, 3 * 60 * 1000);
  }

  async fetchVersionSignature(): Promise<string> {
    try {
      const res = await fetch('/index.html', {
        method: 'HEAD',
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      // ETag o Last-Modified sirven como firma de versión
      return res.headers.get('etag') || res.headers.get('last-modified') || '';
    } catch {
      return '';
    }
  }
}
</script>

<style lang="scss">
/* Global Styles - Sprintly Theme */
*, *::before, *::after {
  font-family: 'Inter', 'Roboto', sans-serif;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  margin: 0 !important;
  padding: 0 !important;
  width: 100%;
  overflow-x: hidden;
}

#app {
  margin: 0 !important;
  padding: 0 !important;
  width: 100%;
  min-height: 100%;
}

/* Eliminar padding interno de v-main de Vuetify */
.v-main,
.v-main__wrap {
  padding: 0 !important;
  padding-top: 0 !important;
  padding-right: 0 !important;
  padding-bottom: 0 !important;
  padding-left: 0 !important;
  margin: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  overflow: visible !important;
}

/* Vuetify v-app no debe tener bordes redondeados ni padding */
.v-application--wrap {
  padding: 0 !important;
  margin: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  min-height: 100vh !important;
}

.v-application {
  width: 100% !important;
  max-width: 100% !important;
}

/* Edge-to-edge mobile (sin márgenes/padding laterales) */
@media (max-width: 768px) {
  html,
  body,
  #app,
  .v-application,
  .v-application--wrap,
  .v-main,
  .v-main__wrap {
    margin: 0 !important;
    padding: 0 !important;
    width: 100vw !important;
    max-width: 100vw !important;
    overflow-x: hidden !important;
  }

  .container,
  .container.fluid,
  .container.fill-height,
  .v-application .container,
  .v-application .container.fluid {
    margin: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    max-width: 100% !important;
  }
}

/* Dark Mode (Default) */
.app {
  background-color: #10111d !important;
  transition: background-color 0.3s ease;
}

/* Light Mode */
.theme--light.v-application {
  background-color: #f5f5f5 !important;
}

.theme--light .app {
  background-color: #f5f5f5 !important;
}

/* Scrollbar personalizado - Dark */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #070d16;
}

.theme--light ::-webkit-scrollbar-track {
  background: #e0e0e0;
}

::-webkit-scrollbar-thumb {
  background: #444444;
  border-radius: 4px;
}

.theme--light ::-webkit-scrollbar-thumb {
  background: #999999;
}

::-webkit-scrollbar-thumb:hover {
  background: #3a3e45;
}

.theme--light ::-webkit-scrollbar-thumb:hover {
  background: #777777;
}

/* Autofill styles - Dark Mode */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
textarea:-webkit-autofill,
textarea:-webkit-autofill:hover,
textarea:-webkit-autofill:focus,
select:-webkit-autofill,
select:-webkit-autofill:hover,
select:-webkit-autofill:focus {
  -webkit-text-fill-color: #ffffff !important;
  border-radius: 4px;
  transition: background-color 5000s ease-in-out 0s;
  -webkit-box-shadow: 0 0 0 30px #2a2a2a inset !important;
}

/* Autofill styles - Light Mode */
.theme--light input:-webkit-autofill,
.theme--light input:-webkit-autofill:hover,
.theme--light input:-webkit-autofill:focus,
.theme--light textarea:-webkit-autofill,
.theme--light textarea:-webkit-autofill:hover,
.theme--light textarea:-webkit-autofill:focus,
.theme--light select:-webkit-autofill,
.theme--light select:-webkit-autofill:hover,
.theme--light select:-webkit-autofill:focus {
  -webkit-text-fill-color: #000000 !important;
  -webkit-box-shadow: 0 0 0 30px #ffffff inset !important;
}
</style>